/**
 * Employees hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { Employee, CreateEmployeeInput, UpdateEmployeeInput } from '@aelita/shared';

interface EmployeeFilters {
  page?: number;
  limit?: number;
  status?: string;
  role?: string;
  department?: string;
  search?: string;
}

export function useEmployees(filters: EmployeeFilters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  });

  return useQuery({
    queryKey: ['employees', filters],
    queryFn: async () => {
      const response = await api.get<Employee[]>(`/employees?${params.toString()}`);
      return response;
    },
  });
}

export function useEmployee(id: string | undefined) {
  return useQuery({
    queryKey: ['employee', id],
    queryFn: async () => {
      const response = await api.get<Employee>(`/employees/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateEmployeeInput) => {
      const response = await api.post<Employee>('/employees', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateEmployeeInput }) => {
      const response = await api.put<Employee>(`/employees/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['employee', id] });
    },
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/employees/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useEmployeeStats(id: string | undefined) {
  return useQuery({
    queryKey: ['employee', id, 'stats'],
    queryFn: async () => {
      const response = await api.get<any>(`/employees/${id}/stats`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useEmployeeProjects(id: string | undefined) {
  return useQuery({
    queryKey: ['employee', id, 'projects'],
    queryFn: async () => {
      const response = await api.get<any[]>(`/employees/${id}/projects`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useEmployeeTasks(id: string | undefined) {
  return useQuery({
    queryKey: ['employee', id, 'tasks'],
    queryFn: async () => {
      const response = await api.get<any[]>(`/employees/${id}/tasks`);
      return response.data;
    },
    enabled: !!id,
  });
}
